import React from 'react';
import { Marker, Tooltip, Popup, Polyline, Circle } from 'react-leaflet';
import L from 'leaflet';
import { dailyFuelStatus, getFuelStatusForDay, timePointToDay, fuelStatusColors } from './bulkFuelData';
import { convoyRouteSegments, alternativeRouteSegment } from './convoyData';
// Create custom icon for fuel status markers
const createFuelStatusIcon = (day: number, percentage: number, status: string) => {
  // Map status to color
  const color = status === 'green' ? '#059669' : status === 'amber' ? '#D97706' : status === 'red' ? '#DC2626' : '#1F2937';
  // Calculate progress bar width based on percentage
  const progressWidth = Math.max(10, Math.min(100, percentage)); // Min 10% for visibility, max 100%
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center; width: 100px;">
        <div style="display: flex; align-items: center; background-color: white; padding: 4px; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); width: 100%;">
          <div style="font-size: 16px; margin-right: 4px;">⛽</div>
          <div style="flex-grow: 1; background-color: #f3f4f6; height: 8px; border-radius: 4px; overflow: hidden;">
            <div style="background-color: ${color}; height: 100%; width: ${progressWidth}%;"></div>
          </div>
          <div style="margin-left: 4px; font-weight: bold; font-size: 12px;">${percentage}%</div>
        </div>
        <div style="background-color: ${color}; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          Day ${day}
        </div>
      </div>
    `,
    className: '',
    iconSize: [100, 50],
    iconAnchor: [50, 25]
  });
};
interface BulkFuelLayerProps {
  selectedTimePoint: string;
  useAlternativeRoute?: boolean;
}
export const BulkFuelLayer: React.FC<BulkFuelLayerProps> = ({
  selectedTimePoint,
  useAlternativeRoute = false
}) => {
  // Convert selected time point to day number
  const currentDay = timePointToDay(selectedTimePoint);
  // Get the route segments based on the selected route
  const routeSegments = convoyRouteSegments.filter(segment => {
    // For day 1, check if we should use the alternative route
    if (segment.day === 1 && useAlternativeRoute) {
      return false;
    }
    return true;
  });
  // Add alternative route if selected
  const allSegments = useAlternativeRoute ? [...routeSegments.filter(segment => segment.day !== 1), alternativeRouteSegment] : routeSegments;
  // Sort segments by day
  const sortedSegments = allSegments.sort((a, b) => a.day - b.day);
  return <>
      {/* Render fuel status markers for each day */}
      {sortedSegments.map(segment => {
      const fuelStatus = getFuelStatusForDay(segment.day);
      // Get the coordinates for the marker - use the last point of each segment
      const markerPosition = segment.coordinates[segment.coordinates.length - 1];
      // Only show days up to the current selected day
      if (segment.day > currentDay && currentDay !== 0) {
        return null;
      }
      return <Marker key={`fuel-status-day-${segment.day}`} position={markerPosition} icon={createFuelStatusIcon(segment.day, fuelStatus.percentageOnHand, fuelStatus.status)} data-id="element-90">
            <Popup data-id="element-91">
              <div className="min-w-[200px]" data-id="element-92">
                <h3 className="font-bold text-base" data-id="element-93">
                  Fuel Status - Day {segment.day}
                </h3>
                <div className="mt-2 space-y-2 text-sm" data-id="element-94">
                  <div data-id="element-95">
                    <span className="font-medium" data-id="element-96">Location: </span>
                    {segment.name.split(': ')[1]}
                  </div>
                  <div data-id="element-97">
                    <span className="font-medium" data-id="element-98">Class III POL Status: </span>
                    <span className={`font-medium ${fuelStatus.status === 'green' ? 'text-green-600' : fuelStatus.status === 'amber' ? 'text-amber-600' : fuelStatus.status === 'red' ? 'text-red-600' : 'text-gray-800'}`} data-id="element-99">
                      {fuelStatus.status === 'green' && '🟢 Green'}
                      {fuelStatus.status === 'amber' && '🟠 Amber'}
                      {fuelStatus.status === 'red' && '🔴 Red'}
                      {fuelStatus.status === 'black' && '⚫ Black'}
                    </span>
                  </div>
                  <div data-id="element-100">
                    <span className="font-medium" data-id="element-101">On Hand: </span>
                    {fuelStatus.percentageOnHand}%
                  </div>
                  <div data-id="element-102">
                    <span className="font-medium" data-id="element-103">Note: </span>
                    {fuelStatus.notes}
                  </div>
                  {segment.day === 5 && <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm" data-id="element-104">
                      <span className="font-medium" data-id="element-105">Resupply Complete</span> -
                      Fuel levels restored to 100%
                    </div>}
                </div>
              </div>
            </Popup>
          </Marker>;
    })}

      {/* Render a summary table when clicking on the route */}
      {sortedSegments.length > 0 && <Polyline positions={sortedSegments.flatMap(segment => segment.coordinates)} pathOptions={{
      color: 'transparent',
      weight: 20,
      opacity: 0.01
    }} data-id="element-106">
          <Popup data-id="element-107">
            <div className="min-w-[280px]" data-id="element-108">
              <h3 className="font-bold text-base mb-2" data-id="element-109">Fuel Status Summary</h3>
              <table className="w-full text-sm border-collapse" data-id="element-110">
                <thead data-id="element-111">
                  <tr className="bg-gray-100" data-id="element-112">
                    <th className="border px-2 py-1 text-left" data-id="element-113">Day</th>
                    <th className="border px-2 py-1 text-left" data-id="element-114">Location</th>
                    <th className="border px-2 py-1 text-left" data-id="element-115">Status</th>
                    <th className="border px-2 py-1 text-right" data-id="element-116">On Hand</th>
                  </tr>
                </thead>
                <tbody data-id="element-117">
                  {dailyFuelStatus.map(status => {
                const segment = sortedSegments.find(s => s.day === status.day);
                const location = segment ? segment.name.split(': ')[1] : '—';
                return <tr key={`summary-day-${status.day}`} className={status.day === 5 ? 'bg-green-50' : ''} data-id="element-118">
                        <td className="border px-2 py-1" data-id="element-119">{status.day}</td>
                        <td className="border px-2 py-1" data-id="element-120">{location}</td>
                        <td className="border px-2 py-1" data-id="element-121">
                          <span className={`inline-block w-3 h-3 rounded-full mr-1 ${status.status === 'green' ? 'bg-green-600' : status.status === 'amber' ? 'bg-amber-600' : status.status === 'red' ? 'bg-red-600' : 'bg-gray-800'}`} data-id="element-122"></span>
                          {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
                        </td>
                        <td className="border px-2 py-1 text-right font-medium" data-id="element-123">
                          {status.percentageOnHand}%
                          {status.day === 5 && <span className="ml-1 text-xs text-green-600" data-id="element-124">
                              (Resupply)
                            </span>}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </Popup>
        </Polyline>}
    </>;
};